import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { History as HistoryIcon, Search, X, Calculator, Loader2, AlertTriangle } from 'lucide-react'
import { getAdminClients, getShippingByCarrier, repriceAllClients } from '../../lib/api'
import AdminClientsTable from '../../components/AdminClientsTable'
import ShippingByCourierTable from '../../components/ShippingByCourierTable'
import { ToastContainer, useToast } from '../../components/Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

export default function AdminHistory() {
  const now = new Date()
  // Default to the previous month (most recent fully-billed period)
  const defaultMonth = now.getMonth() + 1 - 1 || 12
  const defaultYear  = defaultMonth === 12 && now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()

  const [year,  setYear]  = useState(defaultYear)
  const [month, setMonth] = useState(defaultMonth)
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const repriceMutation = useMutation({
    mutationFn: () => repriceAllClients({ year, month, includeArchived: true }),
    onSuccess: (data) => {
      addToast({
        type: 'success',
        message: `Repriced ${data.orders_repriced} order${data.orders_repriced === 1 ? '' : 's'} across ${data.clients_processed} client${data.clients_processed === 1 ? '' : 's'} for ${MONTH_NAMES[month - 1]} ${year}.`,
        duration: 6000,
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients', 'history', year, month] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'shipping-by-carrier', year, month] })
    },
    onError: (err) => addToast({
      type: 'error',
      message: `Reprice failed: ${err.response?.data?.detail || err.message}`,
    }),
  })

  const { data: clients = [], isLoading, error: clientsError } = useQuery({
    queryKey: ['admin', 'clients', 'history', year, month],
    queryFn: () => getAdminClients({ year, month }, { includeArchived: true }),
  })

  const { data: shippingByCarrier = [], isLoading: shippingLoading, error: shippingError } = useQuery({
    queryKey: ['admin', 'shipping-by-carrier', year, month],
    queryFn: () => getShippingByCarrier({ year, month }),
  })

  // Search filter
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return clients
    return clients.filter(c =>
      (c.name     || '').toLowerCase().includes(q) ||
      (c.nickname || '').toLowerCase().includes(q) ||
      (c.email    || '').toLowerCase().includes(q)
    )
  }, [clients, search])

  // Aggregate totals — split into "all" (incl. archived) and "active only".
  const sumField = (rows, field) => rows.reduce((s, c) => s + (c[field] ?? 0), 0)
  const activeOnly = useMemo(() => filtered.filter(c => !c.archived), [filtered])
  const archivedOnly = useMemo(() => filtered.filter(c => c.archived), [filtered])

  const totals = useMemo(() => ({
    mtd_total:             sumField(filtered, 'mtd_total'),
    mtd_orders_fulfilment: sumField(filtered, 'mtd_orders_fulfilment'),
    mtd_orders_shipping:   sumField(filtered, 'mtd_orders_shipping'),
    mtd_sku_charges:       sumField(filtered, 'mtd_sku_charges'),
    mtd_returns:           sumField(filtered, 'mtd_returns'),
    mtd_exports:           sumField(filtered, 'mtd_exports'),
    mtd_storage:           sumField(filtered, 'mtd_storage'),
    mtd_activity:          sumField(filtered, 'mtd_activity'),
  }), [filtered])

  const totalsActive = useMemo(() => ({
    mtd_total:             sumField(activeOnly, 'mtd_total'),
    mtd_orders_fulfilment: sumField(activeOnly, 'mtd_orders_fulfilment'),
    mtd_orders_shipping:   sumField(activeOnly, 'mtd_orders_shipping'),
    mtd_sku_charges:       sumField(activeOnly, 'mtd_sku_charges'),
    mtd_returns:           sumField(activeOnly, 'mtd_returns'),
    mtd_exports:           sumField(activeOnly, 'mtd_exports'),
    mtd_storage:           sumField(activeOnly, 'mtd_storage'),
    mtd_activity:          sumField(activeOnly, 'mtd_activity'),
  }), [activeOnly])

  const archivedTotal       = sumField(archivedOnly, 'mtd_total')
  const archivedClientCount = archivedOnly.length

  // Year picker — current year and a few years back
  const years = useMemo(() => {
    const out = []
    for (let y = now.getFullYear(); y >= 2024; y--) out.push(y)
    return out
  }, [])

  // Quick navigation arrows
  function step(delta) {
    let m = month + delta
    let y = year
    while (m < 1)  { m += 12; y -= 1 }
    while (m > 12) { m -= 12; y += 1 }
    setMonth(m)
    setYear(y)
  }

  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
            <HistoryIcon size={20} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Billing History</h1>
            <p className="text-xs text-slate-400">Per-client totals for any past billing period</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-shrink-0">
            <label className="label">Year</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input w-28">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex-shrink-0">
            <label className="label">Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="input w-40">
              {MONTH_NAMES.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-1 pb-0.5">
            <button onClick={() => step(-1)} className="btn-secondary text-xs py-1.5 px-3" title="Previous month">←</button>
            <button onClick={() => step(+1)} className="btn-secondary text-xs py-1.5 px-3" title="Next month">→</button>
            <button
              onClick={() => repriceMutation.mutate()}
              disabled={repriceMutation.isPending}
              className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
              title={`Re-stamp every order's stored fees for ${MONTH_NAMES[month - 1]} ${year} using each client's current rate card. Includes archived clients so their final-period billing is correct.`}
            >
              {repriceMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
              Reprice
            </button>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="label">Search</label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Filter clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-8"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Period summary tiles. Each tile shows the inclusive total with the
          active-only number underneath in muted grey.

          The error branch exists because `clients` defaults to [] on a failed
          fetch, so every tile would sum to a confident $0.00 for the period. */}
      {clientsError ? (
        <div className="card p-4 bg-red-50 border-red-200 flex items-start gap-2">
          <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Couldn&rsquo;t load {periodLabel} totals</p>
            <p className="text-xs text-red-700 mt-0.5">
              This is <b>not</b> a $0.00 period — the figures are unknown until this loads.
            </p>
            <p className="text-xs text-red-600 font-mono mt-1 break-all">
              {clientsError?.response?.data?.detail || clientsError?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryTile
            label={`${periodLabel} Total`}
            value={formatCurrency(totals.mtd_total)}
            activeValue={formatCurrency(totalsActive.mtd_total)}
            highlight
          />
          <SummaryTile label="Fulfilment"    value={formatCurrency(totals.mtd_orders_fulfilment)} activeValue={formatCurrency(totalsActive.mtd_orders_fulfilment)} />
          <SummaryTile label="Shipping"      value={formatCurrency(totals.mtd_orders_shipping)}   activeValue={formatCurrency(totalsActive.mtd_orders_shipping)} />
          <SummaryTile label="Storage"       value={formatCurrency(totals.mtd_storage)}           activeValue={formatCurrency(totalsActive.mtd_storage)} />
          <SummaryTile label="Order Details" value={formatCurrency(totals.mtd_sku_charges)}       activeValue={formatCurrency(totalsActive.mtd_sku_charges)} />
          <SummaryTile label="Returns"       value={formatCurrency(totals.mtd_returns)}           activeValue={formatCurrency(totalsActive.mtd_returns)} />
          <SummaryTile label="Exports"       value={formatCurrency(totals.mtd_exports)}           activeValue={formatCurrency(totalsActive.mtd_exports)} />
          <SummaryTile label="Activity"      value={formatCurrency(totals.mtd_activity)}          activeValue={formatCurrency(totalsActive.mtd_activity)} />
        </div>
      )}

      {archivedClientCount > 0 && (
        <div className="card p-3 bg-amber-50 border-amber-200 flex items-center justify-between text-sm">
          <span className="text-amber-800">
            {archivedClientCount} archived client{archivedClientCount === 1 ? '' : 's'} contributing
            <span className="font-semibold"> {formatCurrency(archivedTotal)}</span> to {periodLabel} totals.
            Rows highlighted amber in the table below.
          </span>
        </div>
      )}

      {/* Clients table */}
      <div>
        <h3 className="font-semibold text-slate-800 mb-3">All Clients — {periodLabel}</h3>
        {/* Branch here rather than in the table: handing it the [] default
            would render its own "no clients" empty state on a failed fetch. */}
        {clientsError ? (
          <div className="card p-4 bg-red-50 border-red-200 flex items-start gap-2 text-sm">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <span className="text-red-800">
              Couldn&rsquo;t load the client list for {periodLabel} — this is <b>not</b> an empty
              period.{' '}
              <span className="font-mono text-xs text-red-600 break-all">
                {clientsError?.response?.data?.detail || clientsError?.message || 'Unknown error'}
              </span>
            </span>
          </div>
        ) : (
          <AdminClientsTable
            clients={filtered}
            isLoading={isLoading}
            totals={totals}
            totalsActive={totalsActive}
            period={{ year, month }}
          />
        )}
      </div>

      {/* Shipping by Courier. Same guard: the [] default would make the table
          claim "No shipped orders in <period>" when the fetch failed. */}
      {shippingError ? (
        <div className="card p-4 bg-red-50 border-red-200 flex items-start gap-2 text-sm">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-red-800">
            Couldn&rsquo;t load shipping by courier for {periodLabel} — this is <b>not</b> a period
            with no shipping.{' '}
            <span className="font-mono text-xs text-red-600 break-all">
              {shippingError?.response?.data?.detail || shippingError?.message || 'Unknown error'}
            </span>
          </span>
        </div>
      ) : (
        <ShippingByCourierTable rows={shippingByCarrier} isLoading={shippingLoading} periodLabel={periodLabel} />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}

function SummaryTile({ label, value, activeValue, highlight = false }) {
  const showSplit = activeValue && activeValue !== value
  return (
    <div className={`card p-4 ${highlight ? 'bg-gradient-to-br from-blue-50 to-white' : ''}`}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-xl font-bold ${highlight ? 'text-blue-700' : 'text-slate-800'}`}>{value}</p>
      {showSplit && (
        <p className="text-xs text-slate-400 mt-0.5">
          excl. archived: <span className="font-medium">{activeValue}</span>
        </p>
      )}
    </div>
  )
}
